"use client";

import { Clock, Feather, Leaf } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Values() {
  const headerReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const pillars = [{
    numeral: "I.",
    icon: Feather,
    title: "Precision over manual",
    description: "We replace repetitive tasks with surgical AI accuracy. No more human error, no more wasted hours—just streamlined, automated perfection."
  }, {
    numeral: "II.",
    icon: Clock,
    title: "Speed over latency",
    description: "Our systems operate in real-time. We eliminate bottlenecks in your workflow, ensuring your business moves at the speed of modern technology."
  }, {
    numeral: "III.",
    icon: Leaf,
    title: "Intelligence over bloat",
    description: "We build lean, smart integrations. We focus on high-impact automation that scales with your growth, not complex systems that slow you down."
  }];
  return <section data-section-id="1606" id="our-core-values" className="relative bg-background text-foreground py-32 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 lg:px-24">
        {}
        <div ref={headerReveal.ref} className={`relative grid grid-cols-12 gap-8 transition-all duration-700 ease-out ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute right-0 -top-8 col-span-4 col-start-9 select-none font-default font-thin leading-none text-border/30" style={{
          fontSize: "clamp(14rem, 22vw, 22rem)"
        }}>
            02
          </div>

          {}
          <div className="col-span-12 md:col-span-8 relative z-10">
            <p className="font-mono uppercase text-foreground" style={{
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              § 02 — OUR CORE VALUES
            </p>

            <h2 className="font-default text-foreground mt-12 md:mt-14" style={{
            fontWeight: 600,
            fontSize: "clamp(2.25rem, 4.8vw, 4.75rem)",
            letterSpacing: "-0.02em",
            lineHeight: 1.02
          }}>
              Three <span className="text-foreground/70">foundational</span> pillars.
            </h2>
          </div>
        </div>

        {}
        <div className="mt-24 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-16">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarColumn key={i} data-index={i} index={i} numeral={pillar.numeral} title={pillar.title} description={pillar.description} Icon={Icon} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarColumn({
  index,
  numeral,
  title,
  description,
  Icon,
  ...rest
}: {
  index: number;
  numeral: string;
  title: string;
  description: string;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`group relative pt-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${index * 80}ms`
  }}>
      {}
      <div className="absolute top-0 left-0 right-0 flex items-center">
        <div className="h-px flex-1 bg-border transition-colors duration-300 ease-out group-hover:bg-foreground/70" />
        <div className="h-1.5 w-1.5 bg-foreground/70 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100" />
      </div>

      {}
      <div className="text-foreground/70 transition-colors duration-300 ease-out group-hover:text-foreground/70">
        <Icon className="h-12 w-12" strokeWidth={1} />
      </div>

      {}
      <p className="font-mono text-foreground mt-14" style={{
      fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
      letterSpacing: "0.18em",
      lineHeight: 1
    }}>
        {numeral}
      </p>

      {}
      <h3 className="font-default text-foreground mt-4" style={{
      fontWeight: 500,
      fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
      letterSpacing: "-0.01em",
      lineHeight: 1.15
    }}>
        {title}
      </h3>

      {}
      <p className="font-default text-muted-foreground mt-6" style={{
      fontWeight: 400,
      fontSize: "clamp(1.0625rem, 1.15vw, 1.25rem)",
      letterSpacing: "-0.005em",
      lineHeight: 1.55
    }}>
        {description}
      </p>

      {}
      <div className="mt-12">
        <Link to="/AutomationEcosystemPage" className="inline-block font-default text-foreground underline decoration-border underline-offset-[6px] transition-all duration-220 ease-out hover:decoration-foreground/70 hover:underline-offset-4" style={{
        fontSize: "clamp(0.9375rem, 1vw, 1rem)"
      }}>
          Explore our ecosystem
        </Link>
      </div>
    </div>;
}